import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { Alert, Button, Container } from 'react-bootstrap';
import { withTranslation } from 'react-i18next';
import type { WithTranslation } from 'react-i18next';

interface OwnProps {
  children: ReactNode;
}

type Props = OwnProps & WithTranslation;

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error Boundary caught an error:', error, errorInfo);
  }

  private handleReload = () => {
    window.location.reload();
  };

  public render() {
    const { t } = this.props;

    if (this.state.hasError) {
      const isDevelopment = import.meta.env.DEV;
      const isHMRError = this.state.error?.message?.includes('useAuth must be used within') ||
                         this.state.error?.message?.includes('Context') ||
                         this.state.error?.message?.includes('Provider');

      return (
        <Container className="mt-5">
          <Alert variant="danger">
            <Alert.Heading>
              {isDevelopment && isHMRError ? `⚠️ ${t('hmrTitle')}` : `❌ ${t('errorTitle')}`}
            </Alert.Heading>
            <p>
              {isDevelopment && isHMRError ? t('hmrMessage') : t('errorMessage')}
            </p>
            {isDevelopment && (
              <details className="mt-3">
                <summary style={{ cursor: 'pointer' }}>
                  <strong>{t('errorDetails')}</strong>
                </summary>
                <pre className="mt-2 p-2 bg-light rounded">
                  {this.state.error?.message}
                  {'\n\n'}
                  {this.state.error?.stack}
                </pre>
              </details>
            )}
            <hr />
            <div className="d-flex gap-2">
              <Button variant="primary" onClick={this.handleReload}>
                🔄 {t('refreshPage')}
              </Button>
            </div>
          </Alert>
        </Container>
      );
    }

    return this.props.children;
  }
}

export default withTranslation('components', { keyPrefix: 'errorBoundary' })(ErrorBoundary);
